import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useAuth } from '../context/AppContext';
import { categoryService } from '../services/api';
import styles from './CategoryEditorPage.module.css';

const ICON_OPTIONS = ['🏛️', '📈', '🌍', '⚽', '🎭', '💻', '💬', '👥', '📰', '🔬', '🎨', '🏥', '🎓', '🚗'];
const COLOR_OPTIONS = ['#dc2626', '#059669', '#2563eb', '#ea580c', '#7c3aed', '#0891b2', '#4f46e5', '#be185d', '#ca8a04', '#16a34a'];

export default function CategoryEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const isNew = !id;
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    color: '#dc2626',
    icon: '📰',
    order: ''
  });
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isNew) {
      loadData();
    } else {
      // Sugerir orden = max(orden actual) + 10 para respetar los huecos de 10 en 10
      categoryService.getAll()
        .then(cats => {
          const maxOrder = cats.reduce((m, c) => Math.max(m, c.order || 0), 0);
          setFormData(prev => ({ ...prev, order: String(maxOrder + 10) }));
        })
        .catch(() => setFormData(prev => ({ ...prev, order: '10' })));
    }
  }, [id, isAuthenticated, navigate, isNew]);

  const loadData = async () => {
    try {
      setLoading(true);
      const category = await categoryService.getById(id);
      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || '',
        color: category.color || '#dc2626',
        icon: category.icon || '📰',
        order: category.order != null ? String(category.order) : '0'
      });
    } catch (err) {
      console.error('Error loading category:', err);
      setError('Error al cargar la categoría: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: isNew ? generateSlug(name) : prev.slug
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name) {
      setError('El nombre es requerido');
      return;
    }
    
    setSaving(true);

    try {
      const data = {
        name: formData.name,
        slug: formData.slug || generateSlug(formData.name),
        description: formData.description,
        color: formData.color,
        icon: formData.icon,
        order: parseInt(formData.order, 10) || 0
      };

      let response;
      if (isNew) {
        response = await categoryService.create(data);
      } else {
        response = await categoryService.update(id, data);
      }
      
      alert(response.message || 'Categoría guardada correctamente');
      navigate('/admin');
    } catch (err) {
      console.error('Error saving:', err);
      setError('Error al guardar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return <div className={styles.loading}>Cargando...</div>;
  }

  return (
    <main className={styles.editorPage}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/admin" className={styles.backBtn}>
            <ArrowLeft size={20} />
            Volver
          </Link>
          <h1>{isNew ? 'Nueva Categoría' : 'Editar Categoría'}</h1>
        </div>
        <div className={styles.headerActions}>
          <button 
            type="submit" 
            form="categoryForm"
            className={styles.saveBtn}
            disabled={saving}
          >
            <Save size={18} />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <form id="categoryForm" onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.panel}>
          <div className={styles.previewCard} style={{ borderColor: formData.color }}>
            <span className={styles.previewIcon} style={{ backgroundColor: formData.color }}>
              {formData.icon}
            </span>
            <span className={styles.previewName}>{formData.name || 'Nombre de la categoría'}</span>
          </div>

          <div className={styles.field}>
            <label>Nombre *</label>
            <input
              type="text"
              value={formData.name}
              onChange={handleNameChange}
              placeholder="Ej: Tecnología"
              required
            />
          </div>

          <div className={styles.field}>
            <label>Slug (URL)</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="tecnologia"
            />
            <small>Se genera automáticamente del nombre</small>
          </div>

          <div className={styles.field}>
            <label>Descripción</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Breve descripción de la categoría..."
              rows={3}
            />
          </div>

          <div className={styles.field}>
            <label>Orden</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: e.target.value })}
              placeholder="10"
              min="0"
              step="1"
            />
            <small>Menor número aparece primero en el menú. Usa múltiplos de 10 (10, 20, 30...) para dejar huecos y poder insertar entre medias más adelante.</small>
          </div>

          <div className={styles.field}>
            <label>Color</label>
            <div className={styles.colorOptions}>
              {COLOR_OPTIONS.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`${styles.colorBtn} ${formData.color === color ? styles.selected : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setFormData({ ...formData, color })}
                />
              ))}
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className={styles.colorPicker}
                title="Color personalizado"
              />
            </div>
          </div>

          <div className={styles.field}>
            <label>Icono</label>
            <div className={styles.iconOptions}>
              {ICON_OPTIONS.map(icon => (
                <button
                  key={icon}
                  type="button"
                  className={`${styles.iconBtn} ${formData.icon === icon ? styles.selected : ''}`}
                  onClick={() => setFormData({ ...formData, icon })}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>
      </form>
    </main>
  );
}
